import { useState } from 'react';
import { Plus, Star, Users } from 'lucide-react';
import { PageHeader } from '@/components/shared/page';
import { DataTable, type Column } from '@/components/shared/data-table';
import { Can } from '@/components/shared/can';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Checkbox, Select, Switch } from '@/components/ui/misc';
import { FormError } from '@/features/auth/auth-layout';
import { useDebounce } from '@/hooks/use-debounce';
import { usePagination } from '@/hooks/use-pagination';
import { ApiRequestError } from '@/lib/api';
import { useCompanyOptions, useContacts, useSaveContact } from './api';
import type { Contact } from './types';

export function ContactsPage() {
  const pagination = usePagination();
  const [search, setSearch] = useState('');
  const [companyId, setCompanyId] = useState('');
  const [sort, setSort] = useState('');
  const [editing, setEditing] = useState<Contact | null>(null);
  const [creating, setCreating] = useState(false);

  const debounced = useDebounce(search);
  const { data: companies = [] } = useCompanyOptions();
  const { data, isLoading, error, refetch } = useContacts({
    page: pagination.page,
    limit: pagination.limit,
    q: debounced || undefined,
    companyId: companyId || undefined,
    sort: sort || undefined,
  });

  const columns: Column<Contact>[] = [
    {
      key: 'name',
      header: 'Name',
      sortKey: 'first_name',
      cell: (row) => (
        <div className="flex min-w-0 items-center gap-2">
          {row.isPrimary && (
            <span title="Primary contact">
              <Star className="h-3.5 w-3.5 shrink-0 fill-amber-400 text-amber-400" />
            </span>
          )}
          <div className="min-w-0">
            <div className="truncate font-medium">{row.name}</div>
            <div className="truncate text-xs text-muted-foreground">{row.designation ?? '—'}</div>
          </div>
        </div>
      ),
    },
    {
      key: 'company',
      header: 'Company',
      className: 'text-sm',
      cell: (row) => row.company?.name ?? <span className="text-muted-foreground">—</span>,
    },
    {
      key: 'email',
      header: 'Email',
      sortKey: 'email',
      className: 'text-sm',
      cell: (row) => row.email ?? <span className="text-muted-foreground">—</span>,
    },
    {
      key: 'phone',
      header: 'Phone',
      className: 'text-sm text-muted-foreground',
      cell: (row) => row.phone ?? '—',
    },
    {
      key: 'flags',
      header: 'Role',
      cell: (row) =>
        row.isDecisionMaker ? (
          <Badge variant="default">Decision maker</Badge>
        ) : (
          <span className="text-xs text-muted-foreground">—</span>
        ),
    },
    {
      key: 'consent',
      header: 'Consent',
      cell: (row) => (
        <div className="flex gap-1">
          {row.emailOptIn && (
            <Badge variant="secondary" className="text-[10px]">
              Email
            </Badge>
          )}
          {row.whatsappOptIn && (
            <Badge variant="secondary" className="text-[10px]">
              WhatsApp
            </Badge>
          )}
          {!row.emailOptIn && !row.whatsappOptIn && (
            <span className="text-xs text-muted-foreground">None</span>
          )}
        </div>
      ),
    },
    {
      key: 'deals',
      header: 'Deals',
      className: 'tabular text-sm',
      cell: (row) => row.dealCount,
    },
  ];

  return (
    <div>
      <PageHeader
        title="Contacts"
        description="The people at each account, and what they have agreed to be contacted about."
        actions={
          <Can permission="crm.contact.create">
            <Button onClick={() => setCreating(true)}>
              <Plus className="h-4 w-4" />
              New contact
            </Button>
          </Can>
        }
      />

      <div className="mb-4 flex flex-wrap items-center gap-2">
        <Input
          value={search}
          onChange={(event) => {
            setSearch(event.target.value);
            pagination.reset();
          }}
          placeholder="Search name, email, phone or company…"
          className="h-9 w-full sm:max-w-xs"
        />
        <Select
          value={companyId}
          onChange={(event) => {
            setCompanyId(event.target.value);
            pagination.reset();
          }}
          className="h-9 w-auto min-w-[12rem]"
        >
          <option value="">All companies</option>
          {companies.map((company) => (
            <option key={company.id} value={company.id}>
              {company.name}
            </option>
          ))}
        </Select>
      </div>

      <DataTable
        columns={columns}
        rows={data?.data ?? []}
        rowKey={(row) => row.id}
        isLoading={isLoading}
        error={error ? 'Could not load contacts.' : null}
        onRetry={() => void refetch()}
        meta={data?.meta}
        onPageChange={pagination.setPage}
        onRowClick={(row) => setEditing(row)}
        sort={sort}
        onSortChange={setSort}
        emptyTitle="No contacts yet"
        emptyDescription="Add a person, or convert a lead to create one automatically."
        emptyAction={
          <Can permission="crm.contact.create">
            <Button onClick={() => setCreating(true)}>
              <Users className="h-4 w-4" />
              New contact
            </Button>
          </Can>
        }
      />

      {(creating || editing) && (
        <ContactDialog
          contact={editing ?? undefined}
          onClose={() => {
            setCreating(false);
            setEditing(null);
          }}
        />
      )}
    </div>
  );
}

function ContactDialog({ contact, onClose }: { contact?: Contact; onClose: () => void }) {
  const save = useSaveContact();
  const { data: companies = [] } = useCompanyOptions();
  const [error, setError] = useState<string | null>(null);

  const [form, setForm] = useState({
    firstName: contact?.firstName ?? '',
    lastName: contact?.lastName ?? '',
    companyId: contact?.companyId ?? '',
    email: contact?.email ?? '',
    phone: contact?.phone ?? '',
    whatsapp: contact?.whatsapp ?? '',
    designation: contact?.designation ?? '',
    department: contact?.department ?? '',
    linkedin: contact?.linkedin ?? '',
    isPrimary: contact?.isPrimary ?? false,
    isDecisionMaker: contact?.isDecisionMaker ?? false,
    emailOptIn: contact?.emailOptIn ?? false,
    whatsappOptIn: contact?.whatsappOptIn ?? false,
    smsOptIn: contact?.smsOptIn ?? false,
    optInSource: contact?.optInSource ?? '',
  });

  const set = (patch: Partial<typeof form>) => setForm((current) => ({ ...current, ...patch }));

  const submit = async () => {
    setError(null);
    try {
      await save.mutateAsync({ ...(contact ? { id: contact.id } : {}), ...form });
      onClose();
    } catch (caught) {
      setError(caught instanceof ApiRequestError ? caught.message : 'Could not save the contact.');
    }
  };

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>{contact ? contact.name : 'New contact'}</DialogTitle>
        </DialogHeader>

        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="First name">
            <Input
              value={form.firstName}
              autoFocus
              onChange={(e) => set({ firstName: e.target.value })}
            />
          </Field>
          <Field label="Last name">
            <Input value={form.lastName} onChange={(e) => set({ lastName: e.target.value })} />
          </Field>
          <Field label="Company" className="sm:col-span-2">
            <Select value={form.companyId} onChange={(e) => set({ companyId: e.target.value })}>
              <option value="">Not linked</option>
              {companies.map((company) => (
                <option key={company.id} value={company.id}>
                  {company.name}
                </option>
              ))}
            </Select>
          </Field>
          <Field label="Email">
            <Input
              type="email"
              value={form.email}
              onChange={(e) => set({ email: e.target.value })}
            />
          </Field>
          <Field label="Phone">
            <Input value={form.phone} onChange={(e) => set({ phone: e.target.value })} />
          </Field>
          <Field label="WhatsApp">
            <Input value={form.whatsapp} onChange={(e) => set({ whatsapp: e.target.value })} />
          </Field>
          <Field label="Job title">
            <Input value={form.designation} onChange={(e) => set({ designation: e.target.value })} />
          </Field>
          <Field label="Department">
            <Input value={form.department} onChange={(e) => set({ department: e.target.value })} />
          </Field>
          <Field label="LinkedIn">
            <Input value={form.linkedin} onChange={(e) => set({ linkedin: e.target.value })} />
          </Field>

          <div className="space-y-2 sm:col-span-2">
            <label className="flex cursor-pointer items-center gap-2.5">
              <Checkbox
                checked={form.isPrimary}
                onCheckedChange={(checked) => set({ isPrimary: checked === true })}
              />
              <span className="text-sm">
                Primary contact for this company
                <span className="block text-xs text-muted-foreground">
                  Promoting someone demotes whoever held it before.
                </span>
              </span>
            </label>
            <label className="flex cursor-pointer items-center gap-2.5">
              <Checkbox
                checked={form.isDecisionMaker}
                onCheckedChange={(checked) => set({ isDecisionMaker: checked === true })}
              />
              <span className="text-sm">Decision maker</span>
            </label>
          </div>

          <div className="space-y-2 rounded-md border p-3 sm:col-span-2">
            <div className="text-sm font-medium">Marketing consent</div>
            <p className="text-xs text-muted-foreground">
              Only tick what this person has actually agreed to. The source and timestamp are
              recorded the first time each is switched on.
            </p>
            <div className="mt-2 space-y-2">
              {(
                [
                  ['emailOptIn', 'Email'],
                  ['whatsappOptIn', 'WhatsApp'],
                  ['smsOptIn', 'SMS'],
                ] as const
              ).map(([key, label]) => (
                <label key={key} className="flex items-center justify-between">
                  <span className="text-sm">{label}</span>
                  <Switch
                    checked={form[key]}
                    onCheckedChange={(checked) => set({ [key]: checked } as Partial<typeof form>)}
                  />
                </label>
              ))}
            </div>
            {(form.emailOptIn || form.whatsappOptIn || form.smsOptIn) && (
              <div className="mt-2 space-y-1.5">
                <Label className="text-xs">How was consent given?</Label>
                <Input
                  className="h-8"
                  value={form.optInSource}
                  placeholder="Web form, signed contract, verbal at meeting…"
                  onChange={(e) => set({ optInSource: e.target.value })}
                />
              </div>
            )}
            {contact?.optInAt && (
              <p className="text-xs text-muted-foreground">
                Recorded {new Date(contact.optInAt).toLocaleDateString()}
                {contact.optInSource ? ` · ${contact.optInSource}` : ''}
              </p>
            )}
          </div>
        </div>

        <FormError message={error} />

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={submit} disabled={save.isPending || form.firstName.trim().length === 0}>
            {save.isPending ? 'Saving…' : contact ? 'Save changes' : 'Create contact'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Field({
  label,
  className,
  children,
}: {
  label: string;
  className?: string;
  children: React.ReactNode;
}) {
  return (
    <div className={`space-y-1.5 ${className ?? ''}`}>
      <Label>{label}</Label>
      {children}
    </div>
  );
}
